import { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { canonicalModuleOfferLocator, isApiError, sameModuleOfferLocator } from "@bw/shared/api";
import type {
  CommercialAcceptRequest,
  CommercialOfferOwnerView,
} from "@bw/shared/types/generated";
import { useMyOrg } from "@/hooks/use-my-org";
import { useAcceptModuleOffer, useModuleOffer } from "./useModuleOffers";
import { ModuleOfferLifecycleTerms } from "./ModuleOfferLifecycleTerms";
import { ModuleOfferSummary } from "./ModuleOfferSummary";
import {
  ModuleOfferAcceptance,
  ModuleOfferDocument,
} from "./ModuleOfferContent";
import {
  ModuleOfferReadError,
  ModuleOfferWorkspace,
} from "./ModuleOfferWorkspace";

function ReviewController({
  orgId,
  versionId,
}: {
  orgId: string;
  versionId: string;
}) {
  const query = useModuleOffer(orgId, versionId);
  const navigate = useNavigate();
  const [captured, setCaptured] = useState<{
    offer: CommercialOfferOwnerView;
    request: CommercialAcceptRequest;
  } | null>(null);
  const [epoch, setEpoch] = useState(0);
  const [authorityDenied, setAuthorityDenied] = useState(false);
  const readDenied = query.isError && isApiError(query.error) && [401, 403].includes(query.error.status);
  const mutation = useAcceptModuleOffer(orgId, versionId, { revision: epoch, suspended: authorityDenied || readDenied });
  const live = useRef(true);
  const command = useRef<symbol | null>(null);
  useEffect(() => { live.current = true; return () => { live.current = false; command.current = null; }; }, []);
  const terminal =
    isApiError(mutation.error) &&
    mutation.error.status >= 400 &&
    mutation.error.status < 500 &&
    mutation.error.status !== 408 &&
    mutation.error.status !== 429;
  const denied =
    readDenied ||
    (mutation.isError &&
      isApiError(mutation.error) &&
      [401, 403].includes(mutation.error.status));
  const mayPublish = useRef(false);
  const effectLease = useRef(Symbol());
  const lossLatched = useRef(false);
  if (denied && !lossLatched.current) {
    effectLease.current = Symbol();
    lossLatched.current = true;
  }
  mayPublish.current = !denied && !authorityDenied;
  useEffect(() => {
    if (denied) setAuthorityDenied(true);
  }, [denied]);
  if (denied || authorityDenied)
    return (
      <ModuleOfferReadError
        onRetry={async () => {
          const result = await query.refetch();
          if (
            result.isSuccess &&
            sameModuleOfferLocator(result.data.offer_version_id, versionId) &&
            (result.data.audience_org_id === null ||
              sameModuleOfferLocator(result.data.audience_org_id, orgId))
          ) {
            if (!captured || terminal) {
              setCaptured(null);
              mutation.reset();
              setEpoch((value) => value + 1);
            }
            effectLease.current = Symbol();
            lossLatched.current = false;
            setAuthorityDenied(false);
          }
        }}
      />
    );
  if (query.isError && !captured)
    return <ModuleOfferReadError onRetry={() => void query.refetch()} />;
  const offer = captured?.offer ?? query.data;
  if (!offer)
    return <Typography role="status">Loading exact module offer…</Typography>;
  if (
    !sameModuleOfferLocator(offer.offer_version_id, versionId) ||
    (offer.audience_org_id !== null && !sameModuleOfferLocator(offer.audience_org_id, orgId))
  )
    return <ModuleOfferReadError onRetry={() => void query.refetch()} />;
  return (
    <Stack spacing={2.5}>
      <Box component="section" role="region" aria-label="Offer summary">
        <ModuleOfferSummary offer={offer} action={false} />
      </Box>
      <Paper component="section" role="region" aria-label="Agreement content" variant="outlined"
        sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
        <ModuleOfferDocument document={offer.document} headingLevel="h2" />
      </Paper>
      {offer.terms_format === "software_terms_v2" && offer.publication_format === "module_policy_v1" && offer.lifecycle_terms
        ? <Paper component="section" role="region" aria-label="Lifecycle policies" variant="outlined"
          sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
          <ModuleOfferLifecycleTerms terms={offer.lifecycle_terms} />
        </Paper> : null}
      <Paper component="section" role="region" aria-label="Next step" variant="outlined"
        sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>Next step</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Accept the reviewed agreement first. You will confirm billing and module access separately.
            </Typography>
          </Box>
          <ModuleOfferAcceptance
            key={epoch}
            offer={offer}
            attempt={captured && !terminal ? captured.request : null}
            isPending={mutation.isPending || terminal}
            error={
              mutation.isError
                ? terminal
                  ? "Acceptance was denied. Refresh and review before another attempt."
                  : "Acceptance result unknown. Retry the original attempt without changing its terms."
                : null
            }
            onConfirm={(request) => {
              if (!live.current || command.current || mutation.isPending || terminal) return;
              const owned = Symbol(); command.current = owned;
              const ownedLease = effectLease.current;
              const exact = captured?.request ?? Object.freeze({ ...request });
              setCaptured((original) => original ?? { offer, request: exact });
              mutation.mutate(exact, {
                onSuccess: (result) => {
                  if (live.current && mayPublish.current && command.current === owned && effectLease.current === ownedLease
                    && sameModuleOfferLocator(result.org_id, orgId)
                    && sameModuleOfferLocator(result.offer.offer_version_id, exact.offer_version_id)) {
                    navigate(`/commercial/agreements/${result.acceptance_id}`);
                  }
                },
                onSettled: () => { if (command.current === owned) command.current = null; },
              });
            }}
          />
        </Stack>
      </Paper>
      {terminal ? (
        <Button
          size="small"
          variant="outlined"
          onClick={async () => {
            const result = await query.refetch();
            if (result.isSuccess) {
              effectLease.current = Symbol();
              lossLatched.current = false;
              setCaptured(null);
              mutation.reset();
              setEpoch((value) => value + 1);
            }
          }}
        >
          Refresh and review
        </Button>
      ) : null}
    </Stack>
  );
}

export function ModuleOfferReviewPage() {
  const org = useMyOrg();
  const { user } = useAuth();
  const { versionId: requestedVersion } = useParams<{ versionId: string }>();
  const versionId = canonicalModuleOfferLocator(requestedVersion);
  const orgId = canonicalModuleOfferLocator(org.kind === "error" ? undefined : org.org?.org_id);
  return (
    <ModuleOfferWorkspace title="Offer review">
      {org.kind === "loading" ? (
        <Typography role="status">Loading organization…</Typography>
      ) : orgId && versionId && user?.uid ? (
        <ReviewController
          key={`${user.uid}:${orgId}:${versionId}`}
          orgId={orgId}
          versionId={versionId}
        />
      ) : (
        <Typography>Current Owner organization is unavailable.</Typography>
      )}
    </ModuleOfferWorkspace>
  );
}
